import { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Cpu, MemoryStick, Gauge, Users, Activity, AlertTriangle } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";

const fmtBytes = (b) => {
  if (b == null) return "–";
  const gb = b / 1073741824;
  return gb >= 1 ? `${gb.toFixed(2)} GB` : `${(b / 1048576).toFixed(0)} MB`;
};
const nn = (v, s = "") => (v == null ? "–" : `${v}${s}`);

const RANGES = [
  { key: "today", label: "Oggi" },
  { key: "7d", label: "7 giorni" },
  { key: "30d", label: "30 giorni" },
];
const METRICS = [
  { key: "ram_pct", label: "RAM %", color: "#6D5FB8", unit: "%" },
  { key: "cpu", label: "CPU %", color: "#0E9F6E", unit: "%" },
  { key: "lat_p95", label: "Latenza p95", color: "#E0761B", unit: " ms" },
  { key: "active_users", label: "Utenti attivi", color: "#2563EB", unit: "" },
];

function StatCard({ icon: Icon, label, value, sub, tone = "slate", testid }) {
  const toneCls = {
    slate: "text-slate-500", violet: "text-[#6D5FB8]", green: "text-emerald-600",
    orange: "text-orange-600", blue: "text-blue-600", red: "text-red-600",
  }[tone];
  return (
    <Card className="rounded-xl border-slate-200 shadow-sm" data-testid={testid}>
      <CardContent className="p-4">
        <div className="flex items-center gap-2 text-xs font-semibold text-slate-500 mb-1">
          <Icon className={`h-4 w-4 ${toneCls}`} /> {label}
        </div>
        <p className="text-2xl font-heading font-bold text-slate-900 leading-tight">{value}</p>
        {sub && <p className="text-xs text-slate-400 mt-0.5">{sub}</p>}
      </CardContent>
    </Card>
  );
}

export function SystemHealthSection() {
  const [health, setHealth] = useState(null);
  const [range, setRange] = useState("today");
  const [metric, setMetric] = useState("ram_pct");
  const [points, setPoints] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadHealth = useCallback(() => {
    api.get("/admin/system/health").then((r) => setHealth(r.data)).catch(() => {});
  }, []);
  const loadHistory = useCallback((rg) => {
    setLoading(true);
    api.get(`/admin/system/history?range=${rg}`).then((r) => setPoints(r.data?.points || []))
      .catch(() => setPoints([])).finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadHealth(); const t = setInterval(loadHealth, 15000); return () => clearInterval(t); }, [loadHealth]);
  useEffect(() => { loadHistory(range); }, [range, loadHistory]);

  const mconf = METRICS.find((m) => m.key === metric);
  const chartData = points.map((p) => {
    const d = new Date(p.ts);
    const lbl = range === "today"
      ? d.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })
      : range === "7d"
      ? d.toLocaleString("it-IT", { day: "2-digit", month: "short", hour: "2-digit" })
      : d.toLocaleDateString("it-IT", { day: "2-digit", month: "short" });
    return { label: lbl, value: p[metric] };
  });

  return (
    <div className="space-y-5" data-testid="system-health-section">
      <div>
        <h2 className="font-heading text-xl font-bold text-slate-900">Stato del sistema</h2>
        <p className="text-sm text-slate-500">Salute di Orbit in tempo reale e andamento storico. Metriche CPU/RAM lette dal container.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="system-health-cards">
        <StatCard icon={MemoryStick} tone="violet" label="RAM" testid="sh-ram"
          value={nn(health?.ram_pct, "%")} sub={`${fmtBytes(health?.ram_used)} / ${fmtBytes(health?.ram_total)}`} />
        <StatCard icon={Cpu} tone="green" label="CPU" testid="sh-cpu" value={nn(health?.cpu_pct, "%")} sub="uso attuale" />
        <StatCard icon={Gauge} tone="orange" label="Latenza backend" testid="sh-lat"
          value={nn(health?.latency_avg_ms, " ms")} sub={`p95 ${nn(health?.latency_p95_ms, " ms")}`} />
        <StatCard icon={Users} tone="blue" label="Utenti attivi" testid="sh-active"
          value={nn(health?.active_users)} sub="ultimi 5 min" />
        <StatCard icon={Activity} tone="slate" label="Richieste/min" testid="sh-req" value={nn(health?.req_per_min)} sub="backend" />
        <StatCard icon={AlertTriangle} tone={health?.err_5xx_5min > 0 ? "red" : "slate"} label="Errori 5xx" testid="sh-err"
          value={nn(health?.err_5xx_5min)} sub={`${nn(health?.err_rate_5min, "%")} · 5 min`} />
      </div>

      <Card className="rounded-xl border-slate-200 shadow-sm">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <div className="flex flex-wrap gap-1.5" data-testid="sh-metric-selector">
              {METRICS.map((m) => (
                <Button key={m.key} size="sm" variant={metric === m.key ? "default" : "outline"}
                  onClick={() => setMetric(m.key)} data-testid={`sh-metric-${m.key}`}
                  className="rounded-full h-8 text-xs">{m.label}</Button>
              ))}
            </div>
            <div className="flex gap-1.5 bg-slate-100 rounded-lg p-1" data-testid="sh-range-selector">
              {RANGES.map((r) => (
                <button key={r.key} onClick={() => setRange(r.key)} data-testid={`sh-range-${r.key}`}
                  className={`px-3 py-1 rounded-md text-xs font-semibold transition-colors ${range === r.key ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
                  {r.label}
                </button>
              ))}
            </div>
          </div>
          <div className="h-72" data-testid="sh-chart">
            {loading ? (
              <div className="h-full flex items-center justify-center text-sm text-slate-400">Caricamento…</div>
            ) : chartData.length === 0 ? (
              <div className="h-full flex items-center justify-center text-sm text-slate-400" data-testid="sh-chart-empty">
                Nessun dato storico ancora disponibile per questo intervallo.
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData} margin={{ top: 8, right: 16, left: -8, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#EEF0F4" />
                  <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#94A3B8" }} minTickGap={24} />
                  <YAxis tick={{ fontSize: 11, fill: "#94A3B8" }} width={44}
                    domain={metric === "ram_pct" || metric === "cpu" ? [0, 100] : ["auto", "auto"]} />
                  <Tooltip formatter={(v) => (v == null ? "–" : `${v}${mconf?.unit || ""}`)}
                    contentStyle={{ borderRadius: 10, border: "1px solid #E2E8F0", fontSize: 12 }} />
                  <Line type="monotone" dataKey="value" name={mconf?.label} stroke={mconf?.color}
                    strokeWidth={2} dot={false} connectNulls />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default SystemHealthSection;
